import { useCallback, useEffect, useRef, useState } from "react";
import { useSpeechSynthesis } from "react-speech-kit";
import i18n from "../i18n";

const HOVER_DELAY_MS = 150;
const MAX_READABLE_LENGTH = 240;
// High enough that a full landing page is read end-to-end instead of being cut off silently.
const MAX_SCREEN_READABLE_LENGTH = 20000;
const SPEECH_RATE = 0.95;
const SPEECH_PITCH = 1.08;
const SCREEN_READER_ENABLED_MESSAGE =
  "Screen reader enabled. No readable content found on this screen.";
const READABLE_SELECTOR = [
  
  "a[href]",
  "input",
  "textarea",
  "select",
  "label",
  "summary",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "li",
  "td",
  "th",
  "caption",
  "figcaption",
  "[aria-label]",
  "[title]",
  '[role="button"]',
  '[role="link"]',
  '[role="dialog"]',
  '[role="alertdialog"]',
].join(",");

const IGNORE_SELECTOR = '[data-screen-reader-ignore="true"]';
const LANGUAGE_VOICE_MAP = {
  as: "as-IN",
  bn: "bn-IN",
  brx: "hi-IN",
  doi: "hi-IN",
  en: "en-IN",
  gom: "kok-IN",
  gu: "gu-IN",
  hi: "hi-IN",
  kn: "kn-IN",
  kok: "kok-IN",
  ks: "ur-IN",
  mai: "hi-IN",
  ml: "ml-IN",
  mni: "bn-IN",
  mr: "mr-IN",
  ne: "ne-NP",
  or: "or-IN",
  pa: "pa-IN",
  sa: "hi-IN",
  sat: "hi-IN",
  sd: "ur-IN",
  ta: "ta-IN",
  te: "te-IN",
  ur: "ur-IN",
};
const SCRIPT_LANGUAGE_RULES = [
  {
    pattern: /[\u0900-\u097f]/u,
    fallbackLanguage: "hi",
    languageCodes: ["brx", "doi", "gom", "hi", "kok", "mai", "mr", "ne", "sa"],
  },
  {
    pattern: /[\u0980-\u09ff]/u,
    fallbackLanguage: "bn",
    languageCodes: ["as", "bn", "mni"],
  },
  {
    pattern: /[\u0a00-\u0a7f]/u,
    fallbackLanguage: "pa",
    languageCodes: ["pa"],
  },
  {
    pattern: /[\u0a80-\u0aff]/u,
    fallbackLanguage: "gu",
    languageCodes: ["gu"],
  },
  {
    pattern: /[\u0b00-\u0b7f]/u,
    fallbackLanguage: "or",
    languageCodes: ["or"],
  },
  {
    pattern: /[\u0b80-\u0bff]/u,
    fallbackLanguage: "ta",
    languageCodes: ["ta"],
  },
  {
    pattern: /[\u0c00-\u0c7f]/u,
    fallbackLanguage: "te",
    languageCodes: ["te"],
  },
  {
    pattern: /[\u0c80-\u0cff]/u,
    fallbackLanguage: "kn",
    languageCodes: ["kn"],
  },
  {
    pattern: /[\u0d00-\u0d7f]/u,
    fallbackLanguage: "ml",
    languageCodes: ["ml"],
  },
  {
    pattern: /[\u0600-\u06ff]/u,
    fallbackLanguage: "ur",
    languageCodes: ["ks", "sd", "ur"],
  },
  {
    pattern: /[\u1c50-\u1c7f]/u,
    fallbackLanguage: "sat",
    languageCodes: ["sat"],
  },
  {
    pattern: /[\uabc0-\uabff]/u,
    fallbackLanguage: "mni",
    languageCodes: ["mni"],
  },
];
// SpeechSynthesisVoice does not expose gender, so we prefer common female voice names.
const FEMALE_VOICE_NAME_HINTS = [
  "female",
  "aria",
  "catherine",
  "heera",
  "hazel",
  "jenny",
  "kalpana",
  "karen",
  "lekha",
  "linda",
  "natasha",
  "olivia",
  "raveena",
  "samantha",
  "serena",
  "sonia",
  "susan",
  "veena",
  "victoria",
  "zira",
];

const normalizeReadableText = (text = "") =>
  text.replace(/\s+/g, " ").trim();

const trimText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const trimReadableText = (text) => trimText(text, MAX_READABLE_LENGTH);

const getFormControlText = (element) => {
  const labelText = element.labels
    ? Array.from(element.labels)
        .map((label) => label.textContent)
        .join(" ")
    : "";
  const selectedOptionText =
    element.tagName.toLowerCase() === "select"
      ? element.options[element.selectedIndex]?.textContent
      : "";

  return normalizeReadableText(
    [
      labelText,
      element.getAttribute("aria-label"),
      element.getAttribute("placeholder"),
      selectedOptionText,
      element.value,
    ]
      .filter(Boolean)
      .join(" "),
  );
};

const getElementText = (element) => {
  const tagName = element.tagName.toLowerCase();
  if (element.matches('[role="dialog"], [role="alertdialog"]')) {
    return normalizeReadableText(
      ["aria-labelledby", "aria-describedby"].map(attribute =>
        (element.getAttribute(attribute) || "").split(/\s+/)
          .map(id => document.getElementById(id)?.textContent || "").join(" "),
      ).join(" ") || element.textContent,
    );
  }

  if (["input", "textarea", "select"].includes(tagName)) {
    return getFormControlText(element);
  }

  if (tagName === "img") {
    return normalizeReadableText(
      element.getAttribute("alt") ||
        element.getAttribute("aria-label") ||
        element.getAttribute("title"),
    );
  }

  return normalizeReadableText(
    element.getAttribute("aria-label") ||
      element.getAttribute("alt") ||
      element.getAttribute("title") ||
      element.textContent,
  );
};

const getReadableElement = (target) => {
  if (!(target instanceof Element)) return null;
  if (target.closest(IGNORE_SELECTOR)) return null;

  const readableElement = target.closest(READABLE_SELECTOR);

  if (readableElement && !readableElement.closest(IGNORE_SELECTOR)) {
    return readableElement;
  }

  const fallbackText = normalizeReadableText(target.textContent);

  if (fallbackText && fallbackText.length <= MAX_READABLE_LENGTH) {
    return target;
  }

  return null;
};

const getReadableText = (target) => {
  const readableElement = getReadableElement(target);

  if (!readableElement) {
    return { readableElement: null, text: "" };
  }

  const text = readableElement.matches('[role="dialog"], [role="alertdialog"]')
    ? getElementText(readableElement)
    : trimReadableText(getElementText(readableElement));

  return {
    readableElement,
    text,
    speechLanguage: getElementSpeechLanguage(readableElement, text),
  };
};

// Checks that an element is actually rendered (not display:none/collapsed or aria-hidden).
// Deliberately ignores the current scroll position/viewport so the whole page — not just
// whatever happens to be on screen — is picked up in document order.
const isElementRenderVisible = (element) => {
  if (!(element instanceof Element)) return false;
  if (element.closest(IGNORE_SELECTOR)) return false;
  if (element.closest('[aria-hidden="true"], [inert]')) return false;

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
};

const getCurrentScreenItems = () => {
  if (typeof document === "undefined") return [];

  const activeDialog = document.activeElement?.closest('[aria-modal="true"]');
  const readableElements = Array.from(
    (activeDialog || document).querySelectorAll(READABLE_SELECTOR),
  );
  const readableItems = [];
  const seenTexts = new Set();
  let screenTextLength = 0;

  for (const element of readableElements) {
    if (!isElementRenderVisible(element)) continue;

    const text = trimReadableText(getElementText(element));

    if (!text || seenTexts.has(text)) continue;

    readableItems.push({
      element,
      text,
      speechLanguage: getElementSpeechLanguage(element, text),
    });
    seenTexts.add(text);
    screenTextLength += text.length + 2;

    if (screenTextLength >= MAX_SCREEN_READABLE_LENGTH) {
      break;
    }
  }

  return readableItems;
};

const getCurrentScreenText = () =>
  trimText(
    getCurrentScreenItems()
      .map((item) => item.text)
      .join(". "),
    MAX_SCREEN_READABLE_LENGTH,
  );

const getSequenceItemsFromTarget = (readableElement, text, speechLanguage) => {
  const currentScreenItems = getCurrentScreenItems();
  const matchedIndex = currentScreenItems.findIndex(
    (item) => item.element === readableElement,
  );

  if (matchedIndex >= 0) {
    return {
      items: currentScreenItems,
      startIndex: matchedIndex,
    };
  }

  const fallbackItem = {
    element: readableElement,
    text,
    speechLanguage:
      speechLanguage || getElementSpeechLanguage(readableElement, text),
  };
  const nextItemIndex = currentScreenItems.findIndex(
    (item) =>
      readableElement.compareDocumentPosition(item.element) &
      Node.DOCUMENT_POSITION_FOLLOWING,
  );
  const insertIndex =
    nextItemIndex >= 0 ? nextItemIndex : currentScreenItems.length;

  return {
    items: [
      ...currentScreenItems.slice(0, insertIndex),
      fallbackItem,
      ...currentScreenItems.slice(insertIndex),
    ],
    startIndex: insertIndex,
  };
};

const normalizeLanguageCode = (languageCode = "") =>
  languageCode.toLowerCase().replace(/_/g, "-");

const getBaseLanguageCode = (languageCode = "") =>
  normalizeLanguageCode(languageCode).split("-")[0];

const getStoredLanguageCode = () => {
  if (typeof window === "undefined") return "";

  try {
    return (
      window.sessionStorage.getItem("selectedLang") ||
      window.sessionStorage.getItem("bhashini_from_target_lang") ||
      window.localStorage.getItem("preferredLanguage") ||
      ""
    );
  } catch (error) {
    return "";
  }
};

const getActiveLanguageCode = () => {
  const storedLanguage = getStoredLanguageCode();
  const documentLanguage =
    typeof document !== "undefined" ? document.documentElement.lang : "";
  const navigatorLanguage =
    typeof navigator !== "undefined" ? navigator.language : "";

  return getBaseLanguageCode(
    storedLanguage ||
      i18n.resolvedLanguage ||
      i18n.language ||
      documentLanguage ||
      navigatorLanguage ||
      "en",
  );
};

const getSpeechLanguage = (languageCode = "") => {
  const appLanguage = getBaseLanguageCode(
    languageCode || getActiveLanguageCode(),
  );

  if (LANGUAGE_VOICE_MAP[appLanguage]) {
    return LANGUAGE_VOICE_MAP[appLanguage];
  }

  if (typeof document !== "undefined" && document.documentElement.lang) {
    return document.documentElement.lang;
  }

  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }

  return "en-IN";
};

const inferLanguageCodeFromText = (text = "") => {
  const activeLanguage = getActiveLanguageCode();
  const scriptRule = SCRIPT_LANGUAGE_RULES.find((rule) =>
    rule.pattern.test(text),
  );

  if (!scriptRule) return "";

  return scriptRule.languageCodes.includes(activeLanguage)
    ? activeLanguage
    : scriptRule.fallbackLanguage;
};

const getElementLanguageCode = (element, text) => {
  const explicitLanguage = element
    ?.closest?.("[lang]")
    ?.getAttribute("lang");

  return (
    getBaseLanguageCode(explicitLanguage) ||
    inferLanguageCodeFromText(text) ||
    getActiveLanguageCode()
  );
};

const getElementSpeechLanguage = (element, text) =>
  getSpeechLanguage(getElementLanguageCode(element, text));

const hasFemaleVoiceHint = (voice) => {
  const voiceLabel = `${voice.name || ""} ${voice.voiceURI || ""}`;
  return FEMALE_VOICE_NAME_HINTS.some((hint) =>
    voiceLabel.toLowerCase().includes(hint),
  );
};

const scoreVoice = (voice, speechLanguage) => {
  const voiceLanguage = normalizeLanguageCode(voice.lang);
  const targetLanguage = normalizeLanguageCode(speechLanguage);
  const isExactLanguageMatch = voiceLanguage === targetLanguage;
  const isBaseLanguageMatch =
    getBaseLanguageCode(voiceLanguage) === getBaseLanguageCode(targetLanguage);

  let score = 0;

  if (isExactLanguageMatch) score += 60;
  else if (isBaseLanguageMatch) score += 40;
  if (hasFemaleVoiceHint(voice)) score += 30;
  if (voice.default) score += 5;
  if (voiceLanguage.endsWith("-in")) score += 2;

  return score;
};

const isVoiceLanguageMatch = (voice, speechLanguage) => {
  const voiceLanguage = normalizeLanguageCode(voice.lang);
  const targetLanguage = normalizeLanguageCode(speechLanguage);

  return (
    voiceLanguage === targetLanguage ||
    getBaseLanguageCode(voiceLanguage) === getBaseLanguageCode(targetLanguage)
  );
};

const sortVoicesByScore = (voices, speechLanguage) =>
  [...voices].sort(
    (firstVoice, secondVoice) =>
      scoreVoice(secondVoice, speechLanguage) -
      scoreVoice(firstVoice, speechLanguage),
  );

// Devices often lack an installed voice for less common languages (as-IN, kok-IN, or-IN, etc.).
// Rather than staying silent for those languages, fall back through progressively more
// widely-available Indian voices so every language still gets read aloud.
const VOICE_FALLBACK_LANGUAGE_CHAIN = ["hi-IN", "en-IN"];

const getPreferredFemaleVoice = (voices, speechLanguage) => {
  if (!Array.isArray(voices) || voices.length === 0) return null;

  const matchingVoices = voices.filter((voice) =>
    isVoiceLanguageMatch(voice, speechLanguage),
  );

  if (matchingVoices.length > 0) {
    return sortVoicesByScore(matchingVoices, speechLanguage)[0];
  }

  for (const fallbackLanguage of VOICE_FALLBACK_LANGUAGE_CHAIN) {
    const fallbackVoices = voices.filter((voice) =>
      isVoiceLanguageMatch(voice, fallbackLanguage),
    );

    if (fallbackVoices.length > 0) {
      return sortVoicesByScore(fallbackVoices, fallbackLanguage)[0];
    }
  }

  // No known-language voice installed at all — use whatever the device offers
  // instead of leaving the utterance without a voice.
  return sortVoicesByScore(voices, speechLanguage)[0];
};

const useHoverScreenReader = () => {
  const [isScreenReaderActive, setIsScreenReaderActive] = useState(false);
  const speechEndHandlerRef = useRef(() => {});
  const { supported, voices } = useSpeechSynthesis();
  const hoverTimerRef = useRef(null);
  const lastElementRef = useRef(null);
  const lastTextRef = useRef("");
  const sequenceRef = useRef({ items: [], currentIndex: -1, isActive: false });
  const isScreenReaderActiveRef = useRef(isScreenReaderActive);
  const speechRequestIdRef = useRef(0);
  const speechRef = useRef({
    supported,
    voices,
  });

  useEffect(() => {
    speechRef.current = {
      supported,
      voices,
    };
  }, [supported, voices]);

  useEffect(() => {
    isScreenReaderActiveRef.current = isScreenReaderActive;
  }, [isScreenReaderActive]);

  // Some browsers (notably in production/cold-load conditions) return an empty voice
  // list until explicitly queried, so warm it up as soon as this hook mounts.
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return undefined;

    window.speechSynthesis.getVoices();

    const handleVoicesChanged = () => window.speechSynthesis.getVoices();
    window.speechSynthesis.addEventListener?.(
      "voiceschanged",
      handleVoicesChanged,
    );

    return () => {
      window.speechSynthesis.removeEventListener?.(
        "voiceschanged",
        handleVoicesChanged,
      );
    };
  }, []);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const clearSequence = useCallback(() => {
    sequenceRef.current = { items: [], currentIndex: -1, isActive: false };
  }, []);

  const cancelCurrentSpeech = useCallback(() => {
    // Invalidate callbacks before cancel() can dispatch an end/error event.
    speechRequestIdRef.current += 1;
    window.speechSynthesis?.cancel();
  }, []);

  const stopSpeaking = useCallback(() => {
    clearHoverTimer();
    clearSequence();
    cancelCurrentSpeech();
  }, [cancelCurrentSpeech, clearHoverTimer, clearSequence]);

  const speakWithLanguage = useCallback(
    ({ text, voice, speechLanguage }) => {
      if (
        typeof window === "undefined" ||
        !window.speechSynthesis ||
        !window.SpeechSynthesisUtterance
      ) {
        return;
      }

      const utterance = new window.SpeechSynthesisUtterance(text);
      // If a fallback voice (different from the requested language) was chosen, use its own
      // lang so the browser doesn't reject the utterance for an unsupported lang/voice pairing.
      utterance.lang = voice?.lang || speechLanguage;
      utterance.voice = voice;
      utterance.rate = SPEECH_RATE;
      utterance.pitch = SPEECH_PITCH;
      const requestId = speechRequestIdRef.current;
      let finished = false;
      const handleFinished = () => {
        if (finished || requestId !== speechRequestIdRef.current ||
            !isScreenReaderActiveRef.current) return;
        finished = true;
        speechEndHandlerRef.current();
      };
      utterance.onend = handleFinished;
      utterance.onerror = handleFinished;

      window.speechSynthesis.speak(utterance);
    },
    [],
  );

  const speakText = useCallback(
    (text, options = {}) => {
      const { cancelCurrent = true, speechLanguage: requestedLanguage } =
        options;
      const speech = speechRef.current;

      if (!text || !speech.supported) return;

      const speechLanguage = requestedLanguage || getSpeechLanguage();
      const voice = getPreferredFemaleVoice(speech.voices, speechLanguage);
      const currentRequestId = speechRequestIdRef.current + 1;
      const speakCurrentText = () => {
        if (
          speechRequestIdRef.current !== currentRequestId ||
          !isScreenReaderActiveRef.current
        ) {
          return;
        }

        speakWithLanguage({
          text,
          voice,
          speechLanguage,
        });
      };

      if (cancelCurrent) {
        cancelCurrentSpeech();
      }

      speechRequestIdRef.current = currentRequestId;

      if (cancelCurrent) {
        setTimeout(speakCurrentText, 0);
        return;
      }

      speakCurrentText();
    },
    [cancelCurrentSpeech, speakWithLanguage],
  );

  const startReadingSequence = useCallback(
    (items, startIndex = 0) => {
      const nextItem = items[startIndex];

      if (!nextItem) {
        clearSequence();
        speakText(SCREEN_READER_ENABLED_MESSAGE);
        return;
      }

      sequenceRef.current = { items, currentIndex: startIndex, isActive: true };
      lastElementRef.current = nextItem.element;
      lastTextRef.current = nextItem.text;
      nextItem.element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      speakText(nextItem.text, { speechLanguage: nextItem.speechLanguage });
    },
    [clearSequence, speakText],
  );

  const readNextSequenceItem = useCallback(() => {
    const sequence = sequenceRef.current;

    if (!isScreenReaderActiveRef.current || !sequence.isActive) return;

    const nextIndex = sequence.currentIndex + 1;
    const nextItem = sequence.items[nextIndex];

    if (!nextItem) {
      clearSequence();
      return;
    }

    sequenceRef.current = {
      ...sequence,
      currentIndex: nextIndex,
    };
    lastElementRef.current = nextItem.element;
    lastTextRef.current = nextItem.text;
    nextItem.element?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    speakText(nextItem.text, {
      cancelCurrent: false,
      speechLanguage: nextItem.speechLanguage,
    });
  }, [clearSequence, speakText]);

  useEffect(() => {
    speechEndHandlerRef.current = () => {
      readNextSequenceItem();
    };
  }, [readNextSequenceItem]);

  const readCurrentScreen = useCallback(() => {
    const currentScreenItems = getCurrentScreenItems();

    if (currentScreenItems.length === 0) {
      clearSequence();
      speakText(getCurrentScreenText() || SCREEN_READER_ENABLED_MESSAGE);
      return;
    }

    startReadingSequence(currentScreenItems);
  }, [clearSequence, speakText, startReadingSequence]);

  const readTarget = useCallback(
    (target) => {
      if (!isScreenReaderActiveRef.current) return;
      const { readableElement, text, speechLanguage } = getReadableText(target);

      if (!readableElement || !text) return;
      if (readableElement.matches('[role="dialog"], [role="alertdialog"]')) {
        clearHoverTimer();
        startReadingSequence([{ element: readableElement, text, speechLanguage }]);
        return;
      }
      if (
        readableElement === lastElementRef.current &&
        text === lastTextRef.current
      ) {
        return;
      }

      lastElementRef.current = readableElement;
      lastTextRef.current = text;
      clearHoverTimer();

      hoverTimerRef.current = setTimeout(() => {
        if (!isScreenReaderActiveRef.current) return;
        const { items, startIndex } = getSequenceItemsFromTarget(
          readableElement,
          text,
          speechLanguage,
        );
        startReadingSequence(items, startIndex);
      }, HOVER_DELAY_MS);
    },
    [clearHoverTimer, startReadingSequence],
  );

  const clearLastReadTarget = useCallback(() => {
    lastElementRef.current = null;
    lastTextRef.current = "";
  }, []);

  const toggleScreenReader = useCallback(() => {
    if (!supported) return;

    if (isScreenReaderActiveRef.current) {
      isScreenReaderActiveRef.current = false;
      setIsScreenReaderActive(false);
      clearLastReadTarget();
      stopSpeaking();
      return;
    }

    isScreenReaderActiveRef.current = true;
    setIsScreenReaderActive(true);
    clearLastReadTarget();
    // Always start from the top of the page so activating anywhere (e.g. the landing
    // page) reads the content in order instead of from the current scroll position.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    readCurrentScreen();
  }, [
    clearLastReadTarget,
    readCurrentScreen,
    stopSpeaking,
    supported,
  ]);

  useEffect(() => {
    if (!isScreenReaderActive) {
      clearLastReadTarget();
      stopSpeaking();
      return undefined;
    }

    const handleMouseOver = (event) => readTarget(event.target);
    const handleFocusIn = (event) => readTarget(event.target);
    const handleMouseOut = (event) => {
      const readableElement = getReadableElement(event.target);
      const nextTarget = event.relatedTarget;

      if (!readableElement) return;
      if (nextTarget instanceof Node && readableElement.contains(nextTarget)) {
        return;
      }
      if (readableElement === lastElementRef.current) {
        clearLastReadTarget();
        stopSpeaking();
      }
    };

    document.addEventListener("mouseover", handleMouseOver);
    document.addEventListener("mouseout", handleMouseOut);
    document.addEventListener("focusin", handleFocusIn);

    return () => {
      document.removeEventListener("mouseover", handleMouseOver);
      document.removeEventListener("mouseout", handleMouseOut);
      document.removeEventListener("focusin", handleFocusIn);
      stopSpeaking();
    };
  }, [
    clearLastReadTarget,
    isScreenReaderActive,
    readTarget,
    stopSpeaking,
  ]);

  useEffect(() => () => stopSpeaking(), [stopSpeaking]);

  return {
    isScreenReaderActive,
    isSpeechSupported: supported,
    toggleScreenReader,
  };
};

export default useHoverScreenReader;
